// 📅 B341 (AI_150) — Plausibilidade da DATA de um exame importado por PDF
// ─────────────────────────────────────────────────────────────
// Origem (caso real, 31/07/2026): Leonardo fotografou o folhão manuscrito do
// pós-operatório de um transplante. No papel a data está escrita `31/07/26`
// (ano com DOIS dígitos); a extração gravou **2006** no lugar de 2026. Como o
// registro nasceu 20 anos antes do transplante, a linha foi para o fim do
// Folhão e o cálculo de PO virou "Data inválida".
//
// Por que passou: NINGUÉM olhava a data. O sanitizador do backend só cuida de
// valores; a tela de conferência validava apenas o FORMATO (`AAAA-MM-DD`); e a
// trava B296 só barra data FUTURA — 2006 é passado. Falha silenciosa clássica.
//
// Este módulo é a peça determinística: "a IA lê, o código confere"
// ([[rotular-dado-em-vez-de-regra]]). Ele NÃO corrige nada sozinho — apenas
// classifica e SUGERE. Quem decide é o médico na conferência, porque nenhum
// modelo lê manuscrito com 100% de acerto e há exame antigo legítimo.
// ─────────────────────────────────────────────────────────────

import { hojeISO } from "./dataNaoFutura";

export type MotivoDataSuspeita =
  | "futura"
  | "anterior-ao-nascimento"
  | "anterior-ao-transplante"
  | "muito-antiga";

export interface AvaliacaoDataExame {
  /** true quando a data merece conferência explícita antes de salvar. */
  suspeita: boolean;
  motivo?: MotivoDataSuspeita;
  /** Frase curta para o médico (já em português de gente). */
  mensagem: string;
  /**
   * Data ISO provável, quando o padrão é de ano errado (mantém dia/mês e usa o
   * ano mais recente que não caia no futuro). Só é OFERECIDA — nunca aplicada.
   */
  sugestao?: string;
}

/** Extrai 'YYYY-MM-DD' (zero-padded) ou null. */
function soData(valor?: string | null): string | null {
  if (!valor) return null;
  const m = String(valor).trim().match(/^(\d{4})-(\d{1,2})-(\d{1,2})/);
  if (!m) return null;
  return `${m[1]}-${m[2].padStart(2, "0")}-${m[3].padStart(2, "0")}`;
}

/** Quantos anos completos separam duas datas ISO (a − b), aproximado por ano/mês/dia. */
function anosEntre(maisNova: string, maisAntiga: string): number {
  const [aA, aM, aD] = maisNova.split("-").map(Number);
  const [bA, bM, bD] = maisAntiga.split("-").map(Number);
  let anos = aA - bA;
  if (aM < bM || (aM === bM && aD < bD)) anos--;
  return anos;
}

/**
 * Mantém dia/mês e devolve a data com o ANO mais recente que não seja futuro.
 * `31/07/2006` com hoje em `2026-08-02` → `2026-07-31`. É a correção que resolve
 * o caso real (ano de 2 dígitos expandido no século errado) sem adivinhar nada:
 * o dia e o mês vêm do papel, só o ano é reposicionado no presente.
 * Devolve null se não der para formar uma data válida (ex.: 29/02 em ano comum).
 */
export function sugerirAnoAtual(dataISO: string, hoje = hojeISO()): string | null {
  const d = soData(dataISO);
  if (!d) return null;
  const [, mes, dia] = d.split("-");
  const anoHoje = Number(hoje.slice(0, 4));
  for (const ano of [anoHoje, anoHoje - 1]) {
    const candidata = `${ano}-${mes}-${dia}`;
    // Rejeita data inexistente (ex.: 2027-02-29) e data futura.
    const dt = new Date(`${candidata}T12:00:00`);
    const valida =
      dt.getFullYear() === ano &&
      dt.getMonth() + 1 === Number(mes) &&
      dt.getDate() === Number(dia);
    if (valida && candidata <= hoje) return candidata;
  }
  return null;
}

export interface ContextoDataExame {
  /** Data do exame, ISO 'YYYY-MM-DD'. */
  data: string;
  /** Hoje (Brasília). Injetável para teste. */
  hoje?: string;
  /** Data do transplante do paciente, se houver. */
  dataTransplante?: string | null;
  /** Data de nascimento do paciente, se houver. */
  dataNascimento?: string | null;
  /**
   * true quando a importação está acontecendo no contexto PÓS-transplante
   * (Folhão). Aí uma data anterior ao transplante é quase certamente erro:
   * ela quebra o cálculo de PO e some no fim da lista.
   */
  contextoPosTx?: boolean;
  /** Quantos anos para trás já contam como "muito antiga". */
  limiteAnos?: number;
}

/** Anos a partir dos quais uma data de exame importado pede conferência. */
export const LIMITE_ANOS_DATA_ANTIGA = 10;

/**
 * Classifica a data de um exame recém-extraído de PDF. Ordem dos testes = da
 * evidência mais forte para a mais fraca; a primeira que casar é a reportada.
 */
export function avaliarDataExame({
  data,
  hoje = hojeISO(),
  dataTransplante,
  dataNascimento,
  contextoPosTx = false,
  limiteAnos = LIMITE_ANOS_DATA_ANTIGA,
}: ContextoDataExame): AvaliacaoDataExame {
  const d = soData(data);
  // Sem data legível não há o que julgar aqui (o formulário já exige o campo).
  if (!d) return { suspeita: false, mensagem: "" };

  const sugestao = sugerirAnoAtual(d, hoje) ?? undefined;
  // Não faz sentido "sugerir" o que já está lá.
  const sug = sugestao && sugestao !== d ? sugestao : undefined;

  if (d > hoje) {
    return {
      suspeita: true,
      motivo: "futura",
      mensagem: "Esta data está no futuro.",
      sugestao: sug,
    };
  }

  const nasc = soData(dataNascimento);
  if (nasc && d < nasc) {
    return {
      suspeita: true,
      motivo: "anterior-ao-nascimento",
      mensagem: "Esta data é anterior ao nascimento do paciente.",
      sugestao: sug,
    };
  }

  const tx = soData(dataTransplante);
  if (contextoPosTx && tx && d < tx) {
    return {
      suspeita: true,
      motivo: "anterior-ao-transplante",
      mensagem: `Esta data é anterior ao transplante (${tx
        .split("-")
        .reverse()
        .join("/")}) — o exame não entraria no Folhão e o PO não seria calculado.`,
      sugestao: sug,
    };
  }

  if (anosEntre(hoje, d) >= limiteAnos) {
    return {
      suspeita: true,
      motivo: "muito-antiga",
      mensagem: `Esta data é de ${anosEntre(
        hoje,
        d
      )} anos atrás — confira se o ano foi lido corretamente.`,
      sugestao: sug,
    };
  }

  return { suspeita: false, mensagem: "" };
}
